import { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";

/**
 * FilterDropdown Component (Enhanced)
 *
 * ✔ Search inside dropdown
 * ✔ Scrollable list (max height)
 * ✔ Clean UI
 */
export default function FilterDropdown({
  label,
  options = [],
  value,
  onChange,
  placeholder = "Pilih...",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearch(""); // reset search saat close
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const selectedOption = options.find((opt) => opt.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  // 🔍 Filter berdasarkan search
  const filteredOptions = options.filter((opt) =>
    String(opt.label).toLowerCase().includes(search.toLowerCase())
  );

  function handleSelect(optionValue) {
    onChange(optionValue);
    setIsOpen(false);
    setSearch(""); // reset search setelah pilih
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* BUTTON */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center justify-between w-full text-gray-700 bg-white border border-gray-300 hover:border-blue-400 shadow-sm font-medium rounded-lg text-sm px-4 py-2.5 focus:outline-none transition-colors"
        type="button"
      >
        <span>{displayText}</span>
        <ChevronDown
          className={`w-4 h-4 ml-2 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* DROPDOWN */}
      {isOpen && (
        <div className="absolute z-50 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg w-full min-w-[200px]">
          
          {/* 🔍 SEARCH */}
          <div className="p-2 border-b">
            <input
              type="text"
              placeholder="Cari..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>

          {/* 📜 OPTIONS (SCROLLABLE) */}
          <ul className="max-h-60 overflow-y-auto p-2 text-sm text-gray-700 font-medium">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <li key={option.value}>
                  <button
                    onClick={() => handleSelect(option.value)}
                    className={`inline-flex items-center w-full p-2 hover:bg-gray-100 hover:text-gray-900 rounded transition-colors ${
                      value === option.value
                        ? "bg-blue-50 text-blue-700 font-semibold"
                        : ""
                    }`}
                  >
                    {option.label}
                  </button>
                </li>
              ))
            ) : (
              <li className="p-2 text-gray-400 text-center">
                Tidak ditemukan
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
